// Moves attribute values between the API shape (a list) and the form shape (a map
// keyed by name), and formats objects and arrays for editing in a textarea.

const formatObject = (value) => {
  const objectKeys = Object.keys(value);

  if (objectKeys.length === 0) return '{\n}';

  const entries = objectKeys.map((key) => {
    const entry = value[key];
    const rendered = typeof entry === 'boolean' ? entry : JSON.stringify(entry);
    return `${JSON.stringify(key)}: ${rendered}`;
  });

  return `{\n${entries.join(',\n')}\n}`;
};

const formatArray = (value) =>
  `[\n${value.map((e) => `"${e}"`).join(',\n')}\n]`;

// Objects and arrays are shown in a textarea using a hand-rolled pretty printer
// rather than JSON.stringify, because the exact spacing is what users see and edit.
export const formatDisplayValue = (value) => {
  switch (value?.constructor) {
    case Object:
      return formatObject(value);
    case Array:
      return formatArray(value);
    default:
      return value;
  }
};

// A value typed into a textarea that looks like an object is collapsed onto one
// line, otherwise the stored value would grow a newline on every edit.
export const normalizeTextValue = (value) => {
  if (typeof value !== 'string') return value;

  return value.includes('{') ? value.replace(/\n/g, '') : value;
};

export const attributesToValues = (attributes = []) =>
  Object.keys(attributes).reduce((acc, key) => {
    const attribute = attributes[key];
    if (!attribute) return acc;
    return { ...acc, [attribute.name]: attribute.value };
  }, {});

// String values that look like JSON are parsed back into objects/arrays before
// being sent. Anything that fails to parse — or is already an array, as headers
// are — is passed through untouched.
export const serializeValue = (value) => {
  // Headers arrive as tuple arrays and other fields as plain objects; only a
  // string can be carrying an encoded structure.
  if (typeof value !== 'string') return value;

  try {
    return value.includes('{') || value.includes('[')
      ? JSON.parse(value)
      : value;
  } catch (e) {
    return value;
  }
};

export const valuesToAttributes = (values = {}, order = null) => {
  const names = order || Object.keys(values);
  return names.map((name) => ({ name, value: serializeValue(values[name]) }));
};
